import { CourseApi, coursetranchesApi } from "api";
import React from "react";
import { CreateTrancheForm, TrancheFromValues } from "./CreateTrancheForm";
import { useCourseTranches } from "api/hooks";
import { getApiErrorMessage, useToaster } from "utils";
import { formatPrice } from "utils/numberUtils";

import { PaziaButton, PaziaLoader, PaziaWarningInfo } from "components/atoms";
import { TabContent, TabGrid, TabHeader } from "../TabLayout";
import { DeleteButton, EditButton } from "components/atoms/Buttons";
import { CourseTrancheApi } from "api/types";
import { EditTrancheModal } from "components/templates/EditTrancheModal/EditTrancheModal";
import { ToggleReadinessButton } from "components/molecules";
import { coursesStatusApi } from "api/coursesStatusApi";
import { PaziaBox } from "components/atoms";
import { courseRegistrationApi } from "api/registrations";

interface PaymentScheduleStepProps {
  course: CourseApi;
}
export const PaymentScheduleStep = ({ course }: PaymentScheduleStepProps): JSX.Element => {
  const [editTranche, setEditTranche] = React.useState<CourseTrancheApi | undefined>(undefined);
  const { isLoading: loadingTranches, data: apiTranches, refetch, isReady } = useCourseTranches(course.data.id);
  const { showSuccess, showError } = useToaster();

  const onSubmit = async (formData: TrancheFromValues) => {
    const { dueDateOptions, ...rest } = formData;
    try {
      await coursetranchesApi.create(course.data.id, { ...rest });
      showSuccess(`Zapisano transzę ${formData.name}`);
      refetch();
    } catch (error) {
      showError(getApiErrorMessage(error, "Nie udało się zapisać transzy."));
    }
  };

  const deleteTranche = async (id: number) => {
    await coursetranchesApi.delete(course.data.id, id);
    showSuccess("Transza została usunięta.");
    refetch();
  };

  if (loadingTranches) return <PaziaLoader position="center" />;

  return (
    <TabContent>
      <TabGrid>
        <PaziaBox className="flex justify-between lg:col-span-2">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Płatności</h2>
            <div className="mb-2 text-sm">
              Dodaj i zarządzaj transzami płatności dla kursu. <br />
              Określ cenę bazową, którą następnie możesz edytować na ekranie polityki cen.
            </div>
            <ToggleReadinessButton
              isReady={Boolean(isReady)}
              onReady={coursesStatusApi.TRANCHES_READY}
              onUnready={coursesStatusApi.TRANCHES_UNREADY}
              courseId={course.data.id}
              readyText="Transze zostały zatwierdzone"
              invalidate={refetch}
            />
          </div>
          <div></div>
        </PaziaBox>
        <div>
          <CreateTrancheForm onSubmit={onSubmit} dateOfCourse={course.data.dateOfCourse} />
        </div>
        {apiTranches.length > 0 && (
          <div>
            <div className="mb-4 flex flex-col gap-4">
              {apiTranches.map((tranche, idx) => (
                <TrancheRow
                  key={tranche.name}
                  {...tranche}
                  no={idx + 1}
                  onDelete={() => deleteTranche(tranche.id)}
                  onEdit={() => setEditTranche(tranche)}
                />
              ))}
            </div>
            <BulkDueDateUpdate courseId={course.data.id} />
          </div>
        )}
        <EditTrancheModal
          tranche={editTranche}
          dateOfCourse={course.data.dateOfCourse}
          onCloseRequest={async () => {
            setEditTranche(undefined);
            refetch();
          }}
          courseId={course.data.id}
        />
      </TabGrid>
    </TabContent>
  );
};

interface TrancheProps {
  no: number;
  name: string;
  price: number;
  dueDate: Date;
  description: string;
  onDelete: () => void;
  onEdit: () => void;
}

const TrancheRow = ({ no, price, dueDate, name, description, onDelete, onEdit }: TrancheProps) => {
  return (
    <PaziaBox>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <div>
            <span className="font-semibold">{name}</span> -{" "}
            <span className="text-slate-500 opacity-80">
              {dueDate ? dueDate.toLocaleDateString("pl-PL") : "Do 3 dni po zapisie"}
            </span>
          </div>
          <div>{formatPrice(price)}</div>
          <div className="text-sm">{description}</div>
        </div>
        <div className="flex items-center gap-2">
          <EditButton onClick={onEdit} />
          <DeleteButton onClick={onDelete} />
        </div>
      </div>
    </PaziaBox>
  );
};

const BulkDueDateUpdate = ({ courseId }: { courseId: number }) => {
  const [processing, setProcessing] = React.useState(false);
  const { showError, showSuccess } = useToaster();
  const onClick = async () => {
    setProcessing(true);
    try {
      await courseRegistrationApi.updateDueDates(courseId);
      showSuccess("Daty w zgłoszeniach zostały zaktualizowane.");
    } catch (e) {
      showError("Wystąpił błąd podczas aktualizacji dat w zgłoszeniach.");
      console.error(e);
    } finally {
      setProcessing(false);
    }
  };
  return (
    <div className="flex flex-col justify-end gap-2 rounded-sm bg-yellow-100 p-4 text-sm text-black dark:border dark:border-yellow-500/40 dark:bg-yellow-500/10 dark:text-yellow-100">
      <div className="font-semibold">Aktualizacja w zgłoszeniach</div>
      <div>
        W przypadku dokonania modyfikacji w datach zapłat, istnieje możliwość masowej aktualizacji dat w zgłoszeniach
        kursantów.
        <br /> Należy pamiętać o tym, że zmiany dotyczą również ręcznie odroczonych płatności, które zostaną
        zaktualizowane.
      </div>
      <div className="ml-auto">
        <PaziaButton
          type="tertiary"
          processing={processing}
          className="bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
          onClick={onClick}
        >
          Zaktualizuj daty w zgłoszeniach kursantów
        </PaziaButton>
      </div>
    </div>
  );
};
